import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Checkbox,
  Banner,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
// Headless UI directly, not catalyst-ui-kit/radio: the kit is written for
// Tailwind v4 and this app builds with v3, so its radio layout and indicator
// classes are never generated (the selector rendered as empty boxes).
import {
  Fieldset,
  Legend,
  Field,
  Label,
  Description,
  RadioGroup,
  Radio,
} from "@headlessui/react";
import { fetchById } from "services/user-select.service";
import { submit } from "./user-form.slice";
import {
  MEMBERSHIP_KIND_EMPLOYEE,
  MEMBERSHIP_KIND_AUTHORIZED,
  MEMBERSHIP_KIND_GUEST,
  membershipKindToFlags,
  flagsToMembershipKind,
} from "./user-form-helpers";
import PermissionPicker from "components/permission-picker/PermissionPicker";
import DepartmentMultiSelect from "components/department-multi-select/department-multi-select";
import { fetchDepartments } from "services/departments.service";
import UserSelect from "features/user-select/user-select";
import { fetchRole } from "features/auth/authSlice";
import { fetchPermissionCatalogue } from "services/permissions.service";
import { fetchUserPositions } from "services/positions.service";
import { fetchOnboardingsByUser } from "services/onboarding.service";
import { canManageOnboarding, isMaster } from "utils/onboardingPermissions";
import PositionMultiSelect from "features/positions/PositionMultiSelect";
import { sameIdOrder } from "features/positions/positions-helpers";
import UserRolesPanel from "features/work-security-approvals/UserRolesPanel";

const MEMBERSHIP_KIND_OPTIONS = [
  { value: MEMBERSHIP_KIND_EMPLOYEE, labelKey: "membership_kind_employee", hintKey: "membership_kind_employee_hint" },
  { value: MEMBERSHIP_KIND_AUTHORIZED, labelKey: "membership_kind_authorized", hintKey: "membership_kind_authorized_hint" },
  { value: MEMBERSHIP_KIND_GUEST, labelKey: "membership_kind_guest", hintKey: "membership_kind_guest_hint" },
];

function UserForm({ id: userId }) {
  const dispatch = useDispatch();
  const [email, setEmail] = useState();
  const [viewFiles, setViewFiles] = useState();
  const [files, setFiles] = useState();
  const [isDisabled, setIsDisabled] = useState(false);
  const [name, setName] = useState();
  const [phone, setPhone] = useState();
  const [externalCode, setExternalCode] = useState();
  // The list of rights to offer; null until loaded (or when it could not be).
  const [permissionCatalogue, setPermissionCatalogue] = useState(null);
  // Rights given to the person by hand. null = an existing person not loaded
  // yet, so nothing is sent that could wipe their rights.
  const [manualPermissions, setManualPermissions] = useState(userId ? null : []);
  // The manual list as the server last gave it: a save sends it as `expected`,
  // so the server can refuse when someone changed it meanwhile.
  const [loadedManualPermissions, setLoadedManualPermissions] = useState(null);
  // Rights the person's position already brings: shown ticked and locked.
  const [positionPermissionKeys, setPositionPermissionKeys] = useState([]);
  const [manager, setManager] = useState();
  // One of MEMBERSHIP_KIND_EMPLOYEE / _AUTHORIZED / _GUEST; users/post still
  // takes the isUserGuest/isPrivate booleans, derived from this at submit.
  const [membershipKind, setMembershipKind] = useState(MEMBERSHIP_KIND_EMPLOYEE);
  const [isEnableCategoryRules, setIsEnableCategoryRules] = useState(false);
  const [departmentIds, setDepartmentIds] = useState([]);
  const [allDepartments, setAllDepartments] = useState([]);
  const [companyPhone, setCompanyPhone] = useState("");
  // Positions come from the catalogue. After hiring they change only through
  // a contract amendment, so the picker is editable here only by a Master and
  // only for employees without an onboarding record (positions catalogue §1
  // rule 3; users/post is Master-only).
  const [positionIds, setPositionIds] = useState([]);
  const [initialPositionIds, setInitialPositionIds] = useState([]);
  const [hasOnboarding, setHasOnboarding] = useState(null); // null = unknown yet
  // Person-level (shared across companies); picks the feminine position title
  // on documents (positions catalogue §8). "" = not specified.
  const [gender, setGender] = useState("");
  const [initialGender, setInitialGender] = useState("");
  const [errMsg, setErrMsg] = useState("");

  const { t } = useTranslation();
  const { companyId } = useParams();
  const { isLoading } = useSelector((state) => state.userFormReducer);
  const role = useSelector((state) => state.authReducer.role);
  // users/post is Master-only, so only a Master may send positionIds here.
  const isMasterRole = isMaster(role);
  const canSeeGender = isMasterRole || canManageOnboarding(role);
  const canEditPositions =
    isMasterRole && (!userId || hasOnboarding === false);
  const showPermissions = permissionCatalogue !== null && manualPermissions !== null;

  // const handleFormChange = (formData) => dispatch(setFormData(formData));
  const handleFormSubmit = async () => {
    // The API body is unchanged (isUserGuest/isPrivate booleans); the 3-way
    // kind is a UI concept only, so it is derived here rather than sent.
    const { isUserGuest, isPrivate } = membershipKindToFlags(membershipKind);
    const formData = {
      userId,
      email,
      files,
      isDisabled,
      name,
      phone,
      externalCode,
      company: companyId,
      // Only when the rights block is on screen: otherwise the person's rights stay as they are.
      permissions: showPermissions ? manualPermissions : undefined,
      expected: userId && showPermissions ? loadedManualPermissions : undefined,
      manager,
      isUserGuest,
      companyPhone,
      // Free-text position is no longer sent; the backend keeps the text
      // mirror in sync with positionIds.
      positionIds:
        // Order matters (it is the print order): a reorder is a change.
        canEditPositions && !sameIdOrder(positionIds, initialPositionIds)
          ? positionIds
          : undefined,
      // users/post is Master-only; send only an actual change.
      gender: isMasterRole && gender !== initialGender ? gender || null : undefined,
      isEnableCategoryRules,
      isPrivate,
      departmentIds,
    };

    const result = await dispatch(submit(companyId, formData));
    if (result === "stale") {
      // Only after the reload, so a retry sends the rights as they are now.
      await reloadRights();
      toast.error(t("perm_error_stale"));
    }
    if (companyId) dispatch(fetchRole(companyId));
  };

  // Takes the person's rights as the server has them now.
  const applyRights = useCallback((userEntity) => {
    setManualPermissions(userEntity.manualPermissions);
    setLoadedManualPermissions(userEntity.manualPermissions);
    setPositionPermissionKeys(
      userEntity.permissions
        .filter(({ sources }) => sources.some((source) => source.type === "position"))
        .map(({ key }) => key)
    );
  }, []);

  // After a save refused because the rights changed meanwhile: show what is there now.
  const reloadRights = async () => {
    try {
      const { userEntity } = await fetchById(userId, companyId);
      applyRights(userEntity);
    } catch (e) {
      console.error(e);
    }
  };

  const handleEmailChange = useCallback((value) => setEmail(value), []);

  // Handle checkbox state changes generically
  const handleCheckboxChange = useCallback(
    (setter) => (value) => {
      setter(value);
    },
    []
  );

  const handleFilesChange = useCallback((value) => setFiles(value), []);
  const isFormValid = () => email;

  useEffect(() => {
    async function fetchData() {
      try {
        const { userEntity } = await fetchById(userId, companyId);
        setEmail(userEntity.email || "");
        setName(userEntity.name || "");
        setPhone(userEntity.phone || "");
        setExternalCode(userEntity.externalCode || "");
        setViewFiles(userEntity.files);
        setIsDisabled(!!userEntity.isDisabled);
        applyRights(userEntity);
        setManager(userEntity.manager?.id || "");
        setMembershipKind(flagsToMembershipKind(userEntity));
        setCompanyPhone(userEntity.companyPhone || "");
        setIsEnableCategoryRules(!!userEntity.isEnableCategoryRules);
        setDepartmentIds(userEntity.departmentIds || []);
        const storedGender =
          userEntity.gender === "male" || userEntity.gender === "female" ? userEntity.gender : "";
        setGender(storedGender);
        setInitialGender(storedGender);
      } catch (e) {
        console.error(e);
      }
    }
    if (userId) {
      fetchData();
    }
  }, [userId, companyId, applyRights]);

  useEffect(() => {
    if (!companyId || !userId) return undefined;
    let cancelled = false;
    setHasOnboarding(null);
    (async () => {
      try {
        const current = await fetchUserPositions(companyId, userId);
        if (cancelled) return;
        const ids = (current || []).map((p) => p.id);
        setPositionIds(ids);
        setInitialPositionIds(ids);
      } catch (e) {
        if (!cancelled) {
          setPositionIds([]);
          setInitialPositionIds([]);
        }
      }
    })();
    // Whether positions change only via amendments — drives both the
    // editability (Master) and the «Keičiama per sutarties pakeitimą» hint.
    (async () => {
      try {
        const onboardings = await fetchOnboardingsByUser(companyId, userId);
        if (!cancelled) setHasOnboarding((onboardings || []).length > 0);
      } catch (e) {
        // Unknown (e.g. no right to read onboardings): stay read-only
        // without the hint; the backend refuses a change anyway
        // (POSITIONS_VIA_AMENDMENT).
        if (!cancelled) setHasOnboarding(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, userId]);

  useEffect(() => {
    if (!companyId || !userId) return;
    (async () => {
      try {
        const depts = await fetchDepartments(companyId);
        setAllDepartments(
          (depts || []).map((d) => ({ id: d.id, name: d.name }))
        );
      } catch (e) {
        setAllDepartments([]);
      }
    })();
  }, [companyId, userId]);

  // The list of rights the form offers
  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    fetchPermissionCatalogue(companyId)
      .then((catalogue) => {
        if (!cancelled) setPermissionCatalogue(catalogue);
      })
      .catch((e) => console.error("Error fetching the list of permissions:", e));
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const managerHandler = (id) => {
    setManager(id);
  };

  const submitNode = userId ? (
    <Button loading={isLoading} disabled={!isFormValid()} submit>
      {t("update_user")}
    </Button>
  ) : (
    <Button loading={isLoading} disabled={!isFormValid()} submit>
      {t("create_user")}
    </Button>
  );

  return (
    <div className="user-form">
      <Form className="user-form_form" onSubmit={handleFormSubmit}>
        <FormLayout>
          <div className="form-warnings">
            {errMsg && (
              <div className="">
                <Banner tone="critical">{t(errMsg)}</Banner>
              </div>
            )}
          </div>
          <TextField
            disabled={userId}
            label={t("Email")}
            type="email"
            requiredIndicator
            autoComplete="off"
            value={email}
            onChange={handleEmailChange}
          />
          {/* Name, phone and external code live on the global user record and
              are shared with every company the user belongs to — hence the
              helper text when editing an existing user. */}
          <TextField
            label={t("name")}
            type="text"
            autoComplete="off"
            value={name}
            onChange={setName}
            helpText={userId ? t("shared_across_companies") : undefined}
          />
          <TextField
            label={t("phone")}
            type="text"
            autoComplete="off"
            value={phone}
            onChange={setPhone}
            helpText={userId ? t("shared_across_companies") : undefined}
          />
          <TextField
            label={t("external_code")}
            type="text"
            autoComplete="off"
            value={externalCode}
            onChange={setExternalCode}
            helpText={
              userId
                ? `${t("external_code_hint")} ${t("shared_across_companies")}`
                : t("external_code_hint")
            }
          />
          <TextField
            label={t("company_phone")}
            type="text"
            autoComplete="off"
            value={companyPhone}
            onChange={setCompanyPhone}
          />
          {/* The backend returns gender only to Master / Access onboarding;
              anyone else would see a misleading «Nenurodyta». */}
          {canSeeGender && (
          <div>
            <label htmlFor="user-form-gender" className="mb-1 block text-sm font-medium text-gray-700">
              {t("user_gender")}
            </label>
            <select
              id="user-form-gender"
              value={gender}
              onChange={(event) => setGender(event.target.value)}
              disabled={!isMasterRole}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
            >
              <option value="">{t("user_gender_unset")}</option>
              <option value="male">{t("user_gender_male")}</option>
              <option value="female">{t("user_gender_female")}</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">{t("user_gender_hint")}</p>
          </div>
          )}
          {/* Editable for a Master on an employee without onboarding;
              otherwise always shown read-only (empty shows "none"), with the
              amendment hint for an onboarded employee. */}
          {(canEditPositions || userId) && (
            <div>
              <PositionMultiSelect
                companyId={companyId}
                value={positionIds}
                onChange={setPositionIds}
                label={t("positions_title")}
                disabled={!canEditPositions}
              />
              {userId && hasOnboarding && (
                <p className="mt-1 text-xs text-gray-500">
                  {t("positions_via_amendment_hint")}
                </p>
              )}
            </div>
          )}
          {/* Existing employees only — creation has no user id to assign
              pareigybės to yet. Applies immediately via assign/unassign,
              independent of this form's Save (see the panel's own hint). */}
          {userId && companyId && <UserRolesPanel companyId={companyId} userId={userId} />}
          <UserSelect
            onChange={managerHandler}
            value={manager}
            label={"select_manager"}
          />
          {showPermissions && (
            <div>
              <h3 className="text-sm font-semibold text-zinc-950">
                {t("user_form_permissions_title")}
              </h3>
              <p className="mb-3 mt-0.5 text-xs text-gray-500">
                {t("user_form_permissions_hint")}
              </p>
              <PermissionPicker
                catalogue={permissionCatalogue}
                value={manualPermissions}
                positionPermissions={positionPermissionKeys}
                canAssignMaster={isMasterRole}
                onChange={setManualPermissions}
              />
            </div>
          )}
          {userId && allDepartments.length > 0 && (
            <DepartmentMultiSelect
              companyId={companyId}
              value={departmentIds}
              onChange={setDepartmentIds}
              options={allDepartments}
            />
          )}
          <Checkbox
            label={t("disabled")}
            checked={!!isDisabled}
            onChange={handleCheckboxChange(setIsDisabled)}
          />
          <Fieldset>
            <Legend className="text-sm font-semibold text-zinc-950">
              {t("membership_kind")}
            </Legend>
            <RadioGroup
              value={membershipKind}
              onChange={setMembershipKind}
              className="mt-3 space-y-4"
            >
              {MEMBERSHIP_KIND_OPTIONS.map(({ value, labelKey, hintKey }) => (
                <Field key={value} className="flex items-start gap-3">
                  <Radio
                    value={value}
                    className="group mt-0.5 flex size-4 shrink-0 cursor-pointer items-center justify-center rounded-full border border-zinc-400 bg-white data-[checked]:border-zinc-900 data-[checked]:bg-zinc-900 data-[focus]:outline data-[focus]:outline-2 data-[focus]:outline-offset-2 data-[focus]:outline-blue-500"
                  >
                    <span className="invisible size-1.5 rounded-full bg-white group-data-[checked]:visible" />
                  </Radio>
                  <div className="min-w-0">
                    <Label className="block cursor-pointer text-sm font-medium text-zinc-950">
                      {t(labelKey)}
                    </Label>
                    <Description className="text-sm text-zinc-500">
                      {t(hintKey)}
                    </Description>
                  </div>
                </Field>
              ))}
            </RadioGroup>
          </Fieldset>
          <Checkbox
            label={t("enable_category_tree_rule")}
            checked={!!isEnableCategoryRules}
            onChange={handleCheckboxChange(setIsEnableCategoryRules)}
          />
          {submitNode}
        </FormLayout>
      </Form>
    </div>
  );
}

export default UserForm;
